import { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import config from 'constants/config'
import TextButton from 'LuxKit/Button/TextButton'
import SolidAppleIcon from 'LuxKit/Icons/solid/SolidAppleIcon'
import { SOCIAL_LOGIN_ICON_BUTTON_PROPS, SOCIAL_LOGIN_TEXT_BUTTON_PROPS, type SocialLoginButtonVariant } from './SocialLoginButton.constants'
import IconButton from 'LuxKit/Button/IconButton'
import { trackClientEvent } from 'analytics/analytics'
import type { AppleLoginData } from 'actions/AuthActions'
import { jwtDecode } from 'jwt-decode'
import { ensureAppleSdk, isAppleSdkReady } from 'lib/auth/appleSdk'
import LoadingSpinner from 'components/Common/Loading/LoadingSpinner'
import waitForSocialSdk, { SocialSdkTimeoutError } from 'lib/auth/waitForSocialSdk'

interface Props {
  className?: string
  isBlocked?: boolean
  iconOnly?: boolean
  onBlockedClick?: () => void
  onSuccess: (data: AppleLoginData) => void
  onFailure: (data: any) => void
  redirectUri: string
  onPrivateAppleEmailLogin?: (appleLoginData: App.AppleLoginData, email: string) => void
  variant?: SocialLoginButtonVariant
}

function AppleLoginButton(props: Props) {
  const {
    onFailure,
    onSuccess,
    className,
    isBlocked,
    onBlockedClick,
    redirectUri,
    iconOnly,
    onPrivateAppleEmailLogin,
    variant,
  } = props

  const intl = useIntl()
  const [isPending, setIsPending] = useState(false)
  const pendingRef = useRef(false)
  const mountedRef = useRef(true)

  const initializeAppleSdk = useCallback(() => {
    if (!window.AppleID?.auth) {
      throw new Error('Apple SDK failed to load')
    }

    window.AppleID.auth.init({
      clientId: config.APPLE_APP_ID,
      scope: 'name email',
      redirectURI: redirectUri,
      state: 'login',
      usePopup: true,
    })
  }, [redirectUri])

  const prepareAppleSdk = useCallback(async() => {
    await ensureAppleSdk()
    initializeAppleSdk()
  }, [initializeAppleSdk])

  useEffect(() => {
    mountedRef.current = true
    prepareAppleSdk().catch(() => {})
    return () => { mountedRef.current = false }
  }, [prepareAppleSdk])

  const onTryLogin = useCallback(async() => {
    if (pendingRef.current) return

    trackClientEvent({
      subject: 'sign_up_sso_click',
      action: 'apple_auth',
      category: 'logging',
      type: 'operational',
    })

    if (!isAppleSdkReady()) {
      pendingRef.current = true
      setIsPending(true)
      try {
        await waitForSocialSdk(prepareAppleSdk())
        // A slow SDK load can outlive the click's permission to open a popup.
        // Leave the button ready for a fresh click instead of a blocked popup.
        if (!mountedRef.current || !navigator.userActivation?.isActive) return
      } catch(error) {
        if (mountedRef.current && !(error instanceof SocialSdkTimeoutError)) onFailure({ error: 'access_denied' })
        return
      } finally {
        pendingRef.current = false
        if (mountedRef.current) setIsPending(false)
      }
    } else {
      initializeAppleSdk()
    }

    window.AppleID.auth.signIn().then((data) => {
      const { id_token } = data.authorization
      const body = {
        id_token,
        givenName: data.user?.name.firstName,
        firstName: data.user?.name.lastName,
      }

      const { email }: { email: string } = jwtDecode(id_token)

      if (email.endsWith('@privaterelay.appleid.com') && onPrivateAppleEmailLogin) {
        onPrivateAppleEmailLogin(body, email)
      } else {
        onSuccess(body)
      }
    }).catch(onFailure)
  }, [initializeAppleSdk, onFailure, onPrivateAppleEmailLogin, onSuccess, prepareAppleSdk])

  return <>
    {!iconOnly && <TextButton
      {...SOCIAL_LOGIN_TEXT_BUTTON_PROPS}
      variant={variant ?? SOCIAL_LOGIN_TEXT_BUTTON_PROPS.variant}
      startIcon={isPending ? <LoadingSpinner size={20} variant="primary" /> : <SolidAppleIcon />}
      className={className}
      aria-busy={isPending}
      onClick={!isBlocked ? onTryLogin : onBlockedClick}
    >
      {intl.formatMessage({
        id: 'RLeJ3WNANL1J2TDW5T0G',
        defaultMessage: 'Apple',
        description: 'Social login button label for Apple sign-in in the social login button section',
      })}
    </TextButton>}
    {iconOnly && <IconButton
      {...SOCIAL_LOGIN_ICON_BUTTON_PROPS}
      variant={variant ?? SOCIAL_LOGIN_ICON_BUTTON_PROPS.variant}
      className={className}
      aria-busy={isPending}
      onClick={!isBlocked ? onTryLogin : onBlockedClick}
    >
      {isPending ? <LoadingSpinner size={20} variant="primary" /> : <SolidAppleIcon />}
    </IconButton>}
  </>
}

export default AppleLoginButton
